import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";
import { normalizeClientList } from "../utils/normalizeClient";

const basePath = `${API_URL.idp}/system/client`;

const getAllSystemClients = async () => {
  return API.get(
    basePath,
    "system_clients_list",
    {},
    {},
    { headers: { skipJsonContentType: true } }
  ).then(handleResponse);
};

const createSystemClient = async (payload) => {
  return API.post(basePath, "system_client_create", {
    name: payload.name,
    grantType: payload.grantType,
    allowUserCreation: Boolean(payload.allowUserCreation),
    roles: payload.roles || [],
  }).then(handleResponse);
};

const updateSystemClient = async (clientId, payload) => {
  const body = {
    allowUserCreation: Boolean(payload.allowUserCreation),
  };
  if (payload.name !== undefined) body.name = String(payload.name || "").trim();
  if (payload.grantType !== undefined) {
    body.grantType = Array.isArray(payload.grantType)
      ? payload.grantType.filter(Boolean).map(String)
      : [];
  }
  return API.put(`${basePath}/${clientId}`, "system_client_update", body).then(
    handleResponse
  );
};

const updateSystemClientRoles = async (clientId, roles) => {
  return API.put(`${basePath}/${clientId}/role`, "system_client_update_roles", {
    roles,
  }).then(handleResponse);
};

const refreshSystemClientSecret = async (clientId) => {
  return API.put(
    `${basePath}/${clientId}/refreshclientsecret`,
    "system_client_refresh_secret",
    {}
  ).then(handleResponse);
};

const roleAssignmentKeys = (role) => {
  if (role == null) return [];
  if (typeof role === "string") {
    const value = role.trim().toLowerCase();
    return value ? [value] : [];
  }
  return [role.name, role.roleId, role.id, role._id]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((value, index, list) => value && list.indexOf(value) === index);
};

/**
 * Clients store roles by name. After a role is deleted, that name can remain
 * on the client and re-bind if a new role reuses it. Strip it by Client Id.
 */
const detachRoleFromSystemClients = async (role) => {
  const keys = new Set(roleAssignmentKeys(role));
  if (!keys.size) return;

  const clients = normalizeClientList(await getAllSystemClients());
  const updates = clients
    .map((client) => {
      const roles = Array.isArray(client.roles)
        ? client.roles.map(String).filter(Boolean)
        : [];
      const next = roles.filter(
        (item) => !keys.has(String(item).trim().toLowerCase())
      );
      if (next.length === roles.length) return null;
      const clientId = client.clientId || client.id;
      if (!clientId) return null;
      return updateSystemClientRoles(clientId, next);
    })
    .filter(Boolean);

  if (updates.length) {
    await Promise.all(updates);
  }
};

export const systemClientService = {
  getAllSystemClients,
  createSystemClient,
  updateSystemClient,
  updateSystemClientRoles,
  refreshSystemClientSecret,
  detachRoleFromSystemClients,
};
